'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { isLuxePlan } from '@/lib/constants/planLevel';
import { SYNC_COMPLETE_EVENT, type SyncCompleteDetail } from '@/lib/events/syncComplete';

const TOAST_DURATION_MS = 5000;
const FADE_MS = 300;

export default function SyncSuccessToast() {
  const { user, getUserPlan } = useAuth();
  const [visible, setVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [payload, setPayload] = useState<SyncCompleteDetail | null>(null);
  const [isLuxe, setIsLuxe] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsLuxe(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const plan = await getUserPlan();
      if (cancelled) return;
      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('luxe_override, free_until_date, lifetime_access')
        .eq('id', user.id)
        .single();
      const hasOverride =
        profile?.luxe_override ||
        profile?.lifetime_access ||
        (profile?.free_until_date && new Date(profile.free_until_date) > new Date());
      setIsLuxe(hasOverride || isLuxePlan(plan));
    })();
    return () => {
      cancelled = true;
    };
  }, [user, getUserPlan]);

  useEffect(() => {
    if (!isLuxe) return;

    const handleSyncComplete = (e: Event) => {
      const detail = (e as CustomEvent<SyncCompleteDetail>).detail;
      if (!detail || typeof detail.newRecords !== 'number') return;
      setPayload({
        newRecords: detail.newRecords,
        totalCommissionAmount: detail.totalCommissionAmount ?? 0,
      });
      setVisible(true);
      setFadeOut(false);
    };

    window.addEventListener(SYNC_COMPLETE_EVENT, handleSyncComplete);
    return () => window.removeEventListener(SYNC_COMPLETE_EVENT, handleSyncComplete);
  }, [isLuxe]);

  useEffect(() => {
    if (!visible || !payload) return;

    const dismissAt = Date.now() + TOAST_DURATION_MS;
    const startFade = () => {
      setFadeOut(true);
      setTimeout(() => {
        setVisible(false);
        setPayload(null);
      }, FADE_MS);
    };

    const t = setTimeout(startFade, TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [visible, payload]);

  if (!visible || !payload) return null;

  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(payload.totalCommissionAmount);

  return (
    <div
      className="fixed top-6 right-6 z-50 transition-opacity duration-300"
      style={{ opacity: fadeOut ? 0 : 1 }}
      role="status"
      aria-live="polite"
    >
      <div
        className="bg-white px-4 py-3 flex items-center gap-3 shadow-sm min-w-[280px] max-w-[360px]"
        style={{
          borderRadius: '6px',
          border: '1px solid rgba(100, 116, 139, 0.35)',
        }}
      >
        <span
          className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)' }}
          aria-hidden
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </span>
        <div className="flex-1 min-w-0 font-body text-sm text-foreground">
          <p className="font-semibold text-foreground">Sync complete</p>
          <p className="text-slate-600 mt-0.5">
            <span className="font-medium">{payload.newRecords}</span> new record{payload.newRecords !== 1 ? 's' : ''} ·{' '}
            <span className="font-medium">{formattedAmount}</span> verified
          </p>
        </div>
      </div>
    </div>
  );
}
